import {
  Actions,
  Button,
  IconDelete,
  IconEdit,
  IconSecurity,
  Secondary,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import { useState } from 'react';
import { InlineCode } from 'scoobie';

import { TwoColumnGrid } from '#src/components/TwoColumnGrid/TwoColumnGrid.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';
import type { WebhookDetailsQuery } from '#src/types/generated/graphql.ts';

import { UpdateSigningDialog } from './components/UpdateSigning/UpdateSigningDialog.tsx';
import { UpdateDeliveryDialog } from './components/UpdateDelivery/UpdateDeliveryDialog.tsx';
import { DeleteDialog } from './components/DeleteDialog/DeleteDialog.tsx';

const signingAlgorithmLabel = (code: string) => {
  if (code === 'None') {
    return (
      <>
        <IconSecurity tone="critical" /> Unsigned
      </>
    );
  }

  if (code === 'SeekHmacSha512') {
    return (
      <>
        <IconSecurity tone="positive" /> HMAC-SHA512
      </>
    );
  }

  return code;
};

const HirerCell = ({
  hirerId,
  hirer,
}: {
  hirerId: string;
  hirer: NonNullable<WebhookDetailsQuery['webhookSubscription']>['hirer'];
}) => (
  <Text size="small">
    {hirer ? (
      <TextLink href={`/manage/hirers?id=${encodeURIComponent(hirerId)}`}>
        {hirer.name}
      </TextLink>
    ) : (
      <>
        <InlineCode>{hirerId}</InlineCode>
        <Secondary> (no active relationship)</Secondary>
      </>
    )}
  </Text>
);

interface SubscriptionDetailProps {
  subscription: NonNullable<WebhookDetailsQuery['webhookSubscription']>;
}

export const SubscriptionDetail = (props: SubscriptionDetailProps) => {
  const { subscription } = props;

  const [selectedAction, setSelectedAction] = useState<
    'delete' | 'delivery' | 'signing' | null
  >(null);

  const { permissions } = usePermissions();

  const {
    id,
    createDateTime,
    eventTypeCode,
    maxEventsPerAttempt,
    signingAlgorithmCode,
    updateDateTime,
    url,
    hirerId,
    hirer,
  } = subscription;

  return (
    <>
      <Stack space="medium">
        <TwoColumnGrid space="small">
          <Text align="right" size="small" weight="strong">
            ID
          </Text>
          <Text size="small">
            <InlineCode>{id.value}</InlineCode>
          </Text>

          <Text align="right" size="small" weight="strong">
            URL
          </Text>
          <Text size="small">{url}</Text>

          <Text align="right" size="small" weight="strong">
            Scope
          </Text>
          <Text size="small">
            {hirerId ? 'Hirer-specific ' : 'Partner-wide '}
            <InlineCode>{eventTypeCode}</InlineCode>
            {' events'}
          </Text>

          {hirerId && (
            <>
              <Text align="right" size="small" weight="strong">
                Hirer
              </Text>
              <HirerCell hirerId={hirerId.value} hirer={hirer} />
            </>
          )}

          <Text align="right" size="small" weight="strong">
            Batching
          </Text>

          <Text size="small">
            {maxEventsPerAttempt} event
            {maxEventsPerAttempt === 1 ? '' : 's'} per request
          </Text>

          <Text align="right" size="small" weight="strong">
            Signing
          </Text>

          <Text size="small">
            {signingAlgorithmLabel(signingAlgorithmCode)}
          </Text>

          <Text align="right" size="small" weight="strong">
            Created
          </Text>
          <Text size="small">{new Date(createDateTime).toLocaleString()}</Text>

          <Text align="right" size="small" weight="strong">
            Updated
          </Text>
          <Text size="small">{new Date(updateDateTime).toLocaleString()}</Text>
        </TwoColumnGrid>

        {permissions.includes('mutate:webhooks') && (
          <Actions>
            <Button
              size="small"
              icon={<IconEdit />}
              onClick={() => setSelectedAction('delivery')}
              variant="soft"
            >
              Update delivery
            </Button>

            <Button
              size="small"
              icon={<IconSecurity />}
              onClick={() => setSelectedAction('signing')}
              variant="soft"
            >
              Update signing
            </Button>
            <Button
              size="small"
              icon={<IconDelete />}
              onClick={() => setSelectedAction('delete')}
              tone="critical"
              variant="soft"
            >
              Delete
            </Button>
          </Actions>
        )}
      </Stack>

      <UpdateDeliveryDialog
        show={selectedAction === 'delivery'}
        subscription={subscription}
        closeDialog={() => setSelectedAction(null)}
      />

      <UpdateSigningDialog
        show={selectedAction === 'signing'}
        subscription={subscription}
        closeDialog={() => setSelectedAction(null)}
      />

      <DeleteDialog
        show={selectedAction === 'delete'}
        subscriptionId={subscription.id.value}
        closeDialog={() => setSelectedAction(null)}
      />
    </>
  );
};
